import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API_BASE, safeBlobDownload } from "@/lib/api";
import { formatInr } from "@/lib/currency";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Stack, DownloadSimple, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

function tokenHeaders() {
  const t = localStorage.getItem("aig_access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}
async function downloadUrl(url, filename) {
  await safeBlobDownload(url, filename, (msg) => toast.error(msg));
}

export function BatchesPage() {
  const [batches, setBatches] = useState([]);
  useEffect(() => { api.get("/imports/batches").then((r) => setBatches(r.data)); }, []);

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Import Batches</h1>
        <p className="text-sm text-muted-foreground mt-1">Every upload is recorded. Preview rows, commit, or download reconciliation.</p>
      </div>
      <div className="card-panel">
        <table className="w-full table-dense" data-testid="batches-table">
          <thead className="bg-muted/60">
            <tr>
              <th className="text-left">File</th>
              <th className="text-left">Uploaded</th>
              <th className="text-right">Rows</th>
              <th className="text-right">Valid</th>
              <th className="text-right">Invalid</th>
              <th className="text-left">Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => (
              <tr key={b.id} className="border-t border-border/60 hover:bg-muted/40" data-testid={`batch-row-${b.id}`}>
                <td className="font-medium">
                  <Link to={`/batches/${b.id}`} className="hover:underline flex items-center gap-2">
                    <Stack size={16} weight="duotone" /> {b.filename}
                  </Link>
                </td>
                <td className="text-xs text-muted-foreground">{new Date(b.created_at).toLocaleString()}</td>
                <td className="num-cell">{b.total_rows}</td>
                <td className="num-cell text-emerald-700">{b.valid_rows}</td>
                <td className="num-cell text-rose-700">{b.invalid_rows}</td>
                <td><StatusBadge status={b.status} /></td>
                <td className="text-right">
                  <Link to={`/batches/${b.id}`} className="text-primary text-sm hover:underline">Open</Link>
                </td>
              </tr>
            ))}
            {batches.length === 0 && (
              <tr><td colSpan="7" className="text-center py-8 text-muted-foreground">No batches yet. Upload an Excel to get started.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function BatchDetailPage() {
  const { id } = useParams();
  const [batch, setBatch] = useState(null);
  const [invoices, setInvoices] = useState([]);
  useEffect(() => {
    api.get(`/imports/batches/${id}`).then((r) => setBatch(r.data));
    api.get("/invoices", { params: { batch_id: id, limit: 1000 } }).then((r) => setInvoices(r.data)).catch(() => {});
  }, [id]);
  if (!batch) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const isCommitted = batch.status === "committed";

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-muted-foreground"><Link to="/batches" className="hover:underline">Batches</Link></div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Stack size={26} weight="duotone" /> {batch.filename}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Uploaded {new Date(batch.created_at).toLocaleString()} by {batch.uploaded_by_email}
          </p>
        </div>
        <div className="flex gap-2">
          {isCommitted && (
            <>
              <Button variant="outline" onClick={() => downloadUrl(`${API_BASE}/imports/batches/${id}/reconciliation.xlsx`, `reconciliation_${id}.xlsx`)} data-testid="reconciliation-xlsx-btn">
                <DownloadSimple size={16} className="mr-2" /> Reconciliation .xlsx
              </Button>
              <Button variant="outline" onClick={() => downloadUrl(`${API_BASE}/invoices/batch/${id}/download-zip`, `batch_${id}.zip`)} data-testid="batch-zip-btn">
                <DownloadSimple size={16} className="mr-2" /> Approved ZIP
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="kpi-card"><div className="text-xs uppercase text-muted-foreground">Total Rows</div><div className="text-xl font-display font-semibold mt-1">{batch.total_rows}</div></div>
        <div className="kpi-card"><div className="text-xs uppercase text-muted-foreground">Valid</div><div className="text-xl font-display font-semibold mt-1 text-emerald-700">{batch.valid_rows}</div></div>
        <div className="kpi-card"><div className="text-xs uppercase text-muted-foreground">Invalid</div><div className="text-xl font-display font-semibold mt-1 text-rose-700">{batch.invalid_rows}</div></div>
        <div className="kpi-card" data-testid="batch-invoices-generated"><div className="text-xs uppercase text-muted-foreground">Invoices Generated</div><div className="text-xl font-display font-semibold mt-1">{isCommitted ? (batch.created_invoices ?? invoices.length) : "—"}</div></div>
        <div className="kpi-card"><div className="text-xs uppercase text-muted-foreground">Status</div><div className="mt-2"><StatusBadge status={batch.status} /></div></div>
      </div>

      {isCommitted && invoices.length > 0 && (
        <div className="card-panel" data-testid="generated-invoices-panel">
          <div className="p-4 border-b border-border font-medium flex items-center justify-between">
            <span>Generated Invoices ({invoices.length})</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full table-dense">
              <thead className="bg-muted/60">
                <tr>
                  <th className="text-left">Invoice #</th>
                  <th className="text-left">Issuer</th>
                  <th className="text-right">Amount</th>
                  <th className="text-left">Date</th>
                  <th className="text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-border/60 hover:bg-muted/40" data-testid={`gen-invoice-${inv.id}`}>
                    <td className="font-mono text-xs">{inv.invoice_number}</td>
                    <td>{inv.issuer_name || inv.party_name || "—"}</td>
                    <td className="num-cell">{formatInr(inv.amount)}</td>
                    <td className="text-xs">{inv.invoice_date}</td>
                    <td><StatusBadge status={inv.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card-panel">
        <div className="p-4 border-b border-border font-medium">Rows</div>
        <div className="overflow-x-auto">
          <table className="w-full table-dense">
            <thead className="bg-muted/60">
              <tr>
                <th className="text-left">Row</th>
                <th className="text-left">Party</th>
                <th className="text-right">Total</th>
                <th className="text-left">Invoice Date</th>
                <th className="text-left">Template</th>
                <th className="text-left">Errors</th>
              </tr>
            </thead>
            <tbody>
              {(batch.rows || []).map((r) => (
                <tr key={r.row_number} className="border-t border-border/60">
                  <td className="num-cell text-left">{r.row_number}</td>
                  <td>{r.issuer_name || r.party_name || "—"}</td>
                  <td className="num-cell">{r.total_amount ? formatInr(r.total_amount) : "—"}</td>
                  <td className="text-xs">{r.invoice_date || "—"}</td>
                  <td className="text-xs">{r.template_code || "auto"}</td>
                  <td className="text-xs text-rose-700">{r.errors?.join(" · ") || (r.is_valid ? "" : "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
